import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { concatMap, distinctUntilChanged, filter, takeUntil, tap } from 'rxjs/operators';
import { CepService } from 'src/app/app-core/lib/cep.service';
import { IbgeService } from 'src/app/app-core/lib/ibge.service';

@Component({
    selector: 'app-transmitter-itbi',
    templateUrl: './transmitter-itbi.component.html',
    styleUrls: ['./transmitter-itbi.component.scss'],
    providers: [IbgeService, CepService]
})
export class TransmitterItbiComponent implements OnInit, OnChanges, OnDestroy {
    @Output() public onTransmitterChange = new EventEmitter();
    @Output() duplicarFormularioChange = new EventEmitter<boolean>();
    @Input() isRepresentingAnotherPerson: boolean = false;
    @Input() transmitterDetails;
    @Input() transmitterDisabled: boolean = false;

    public form: FormGroup;
    public estados: [];
    public municipiosPorEstado: any[];
    public municipiosPorEstadoMap = new Map<number, any[]>();
    public estadosMap = new Map<number, any[]>();
    public duplicarFormulario = false;
    private destroy$ = new Subject();

    constructor(
        private fb: FormBuilder,
        private ibgeService: IbgeService,
        private cepService: CepService
    ) { }

    ngOnInit() {
        this.initForm();
        this.formChanges();
        this.loadUfs();
        this.loadMunicipiosByUF();
        this.handleFindCep();
        this.form.get('duplicarFormulario').valueChanges.subscribe(() => {
            this.handleDuplicado();
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!this.form) return;

        if (changes['transmitterDetails'] && this.transmitterDetails && !this.transmitterDisabled) {
            this.changeDataRequester();
            this.form.get('CPF')?.disable();
            this.form.get('nome')?.disable();
            this.form.get('email')?.disable();
            this.form.get('telefone')?.disable();
        }
    }


    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.unsubscribe();
    }

    public changeDataRequester() {
        if (!this.transmitterDetails) return;

        const currentCpf = this.form.get('CPF')?.value;
        const currentNome = this.form.get('nome')?.value;
        const currentEmail = this.form.get('email')?.value;
        const currentTelefone = this.form.get('telefone')?.value;

        const newCpf = this.transmitterDetails.SOLR_CpfCnpj;
        const newNome = this.transmitterDetails.SOLR_NomeRazaoSocial;
        const newEmail = this.transmitterDetails.SOLR_Email;
        const newTelefone = this.transmitterDetails.SOLR_Telefone;

        if (currentCpf === newCpf && currentNome === newNome && currentEmail === newEmail && currentTelefone === newTelefone) return;

        this.form.patchValue({
            CPF: newCpf,
            nome: newNome,
            email: newEmail,
            telefone: newTelefone
        });

        this.form.updateValueAndValidity();
    }

    private loadUfs() {
        this.ibgeService.ufs.subscribe({
            next: (response: any) => {
                this.estados = response;
            }
        });
    }

    public loadMunicipiosByUF() {
        this.form.get('uf').valueChanges.subscribe((value: any) => {
            this.ibgeService.municipiosByUf(value).subscribe({
                next: (response: any) => {
                    this.municipiosPorEstado = response;
                }
            });
        })
    }

    public handleFindCep() {
        this.form.get('CEP').valueChanges.subscribe({
            next: (value: string) => {
                if (value.length > 7) {
                    this.cepService.getEnderecoByCep(value).subscribe({
                        next: (response: any) => {
                            console.log('[endereco-form.handleFindCep] response: ', response);
                            this.form.patchValue({
                                bairro: response.bairro,
                                logradouro: response.logradouro,
                                localidade: response.localidade
                            });

                            if (!this.form.get('uf').value || this.form.get('uf').value != response.uf) {
                                this.form.get('uf').setValue(response.uf);
                            }

                            this.form.updateValueAndValidity();
                        }
                    });
                }
            }
        });
    }


    private initForm() {
        this.form = this.fb.group({
            uf: ['', Validators.required],
            localidade: ['', Validators.required],
            logradouro: ['', Validators.required],
            numero: ['', Validators.required],
            complemento: [''],
            bairro: ['', Validators.required],
            CEP: ['', Validators.required],
            CPF: ['', Validators.required],
            nome: ['', Validators.required],
            telefone: ['', Validators.required],
            email: ['', Validators.required],
            duplicarFormulario: [false],
            duplicados: this.fb.array([])
        });
    }

    get duplicados(): FormArray {
        return this.form.get('duplicados') as FormArray;
    }

    get isDuplicarFormulario(): boolean {
        return this.form.get('duplicarFormulario')?.value;
    }

    handleDuplicado(): void {
        const duplicar = this.form.get('duplicarFormulario')?.value;
        this.duplicarFormularioChange.emit(duplicar);

        if (duplicar && this.duplicados.length === 0) {
            this.adicionarDuplicado();
        } else if (!duplicar) {
            this.limparDuplicados();
        }
    }

    adicionarDuplicado() {
        const grupo = this.fb.group({
            nomeAdicional: ['', Validators.required],
            cpfCnpjAdicional: ['', Validators.required],
            cepAdicional: ['', Validators.required],
            ufAdicional: ['', Validators.required],
            municipioAdicional: ['', Validators.required],
            logradouroAdicional: ['', Validators.required],
            numeroAdicional: ['', Validators.required],
            complementoAdicional: [null],
            bairroAdicional: ['', Validators.required],
        });

        this.duplicados.push(grupo);
        const index = this.duplicados.length - 1;

        this.handleFindCepDuplicado(grupo, index);
        this.loadUfsDuplicado(grupo, index);
        this.loadMunicipiosByUFDuplicado(grupo, index);

        this.onTransmitterChange.emit(this.form.getRawValue());
    }

    removerDuplicado(index: number): void {
        this.duplicados.removeAt(index);
        this.onTransmitterChange.emit(this.form.getRawValue());
    }

    limparDuplicados(): void {
        while (this.duplicados.length !== 0) {
            this.duplicados.removeAt(0);
        }
    }

    private handleFindCepDuplicado(grupo: FormGroup, index: number) {
        grupo.get('cepAdicional')?.valueChanges.pipe(
            filter((value: string) => !!value && value.length > 7),
            distinctUntilChanged(),
            takeUntil(this.destroy$),
            concatMap((cep: string) =>
                this.cepService.getEnderecoByCep(cep).pipe(
                    concatMap((response: any) => {
                        grupo.patchValue({
                            bairroAdicional: response.bairro,
                            logradouroAdicional: response.logradouro
                        });

                        grupo.get('ufAdicional')?.setValue(response.uf, { emitEvent: false });

                        return this.ibgeService.municipiosByUf(response.uf).pipe(
                            tap((municipios: any[]) => {
                                this.municipiosPorEstadoMap.set(index, municipios);

                                const municipioExiste = municipios.find(m => m.nome === response.localidade);
                                grupo.get('municipioAdicional')?.setValue(
                                    municipioExiste ? response.localidade : ''
                                );

                                grupo.updateValueAndValidity({ emitEvent: true });
                                this.onTransmitterChange.emit(this.form.getRawValue());
                            })
                        );
                    })
                )
            )
        ).subscribe();
    }

    public loadUfsDuplicado(grupo: FormGroup, index: number) {
        this.ibgeService.ufs.subscribe({
            next: (response: any) => {
                this.estadosMap.set(index, response);
                grupo.get('ufAdicional')?.updateValueAndValidity();
            }
        });
    }

    public loadMunicipiosByUFDuplicado(grupo: FormGroup, index: number) {
        grupo.get('ufAdicional')?.valueChanges.pipe(
            distinctUntilChanged(),
            filter(value => !!value),
            concatMap((uf: string) =>
                this.ibgeService.municipiosByUf(uf).pipe(
                    tap((municipios: any[]) => {
                        this.municipiosPorEstadoMap.set(index, municipios);

                        const municipioAtual = grupo.get('municipioAdicional')?.value;
                        const municipioExiste = municipios.some(m => m.nome === municipioAtual);

                        if (!municipioExiste) {
                            grupo.get('municipioAdicional')?.setValue('');
                        }

                        grupo.get('municipioAdicional')?.updateValueAndValidity();
                    })
                )
            )
        ).subscribe();
    }

    private formChanges() {
        this.form.valueChanges.subscribe(() => {
            this.onTransmitterChange.emit(this.form.getRawValue());
        });
    }

    /**
     * Dados do rascunho. Os campos travados (CPF, nome, e-mail e telefone de quem está
     * logado ou representado) continuam com o que o formulário já preencheu.
     */
    public restoreDraft(valores: any): void {
        if (!valores) return;

        const { CEP, duplicarFormulario, duplicados, ...demais } = valores;
        const liberados: any = {};

        Object.keys(demais)
            .filter(campo => this.form.get(campo)?.enabled)
            .forEach(campo => liberados[campo] = demais[campo]);

        this.form.patchValue(liberados);
        // Sem a busca do CEP, que trocaria o endereço preenchido pelo da base dos Correios.
        this.form.get('CEP').setValue(CEP ?? '', { emitEvent: false });

        if (duplicarFormulario && Array.isArray(duplicados) && duplicados.length) {
            // Marcar o checkbox cria o primeiro transmitente adicional (handleDuplicado); os demais, aqui
            this.form.get('duplicarFormulario').setValue(true);

            for (let i = this.duplicados.length; i < duplicados.length; i++) {
                this.adicionarDuplicado();
            }

            duplicados.forEach((duplicado, index) => {
                const { cepAdicional, ...endereco } = duplicado || {};
                const grupo = this.duplicados.at(index);

                grupo.patchValue(endereco);
                grupo.get('cepAdicional').setValue(cepAdicional ?? '', { emitEvent: false });
            });
        }

        this.form.updateValueAndValidity();
        this.onTransmitterChange.emit(this.form.getRawValue());
    }

    reset() {
        this.form.patchValue({
            CPF: '',
            nome: '',
            email: '',
            telefone: '',
            uf: '',
            localidade: '',
            logradouro: '',
            numero: '',
            complemento: '',
            bairro: '',
            CEP: ''
        });
        this.form.enable();
        this.form.updateValueAndValidity();
    }

}